import { JSX, useContext, useEffect, useMemo } from "react";
import { CheckboxStateEnum, QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import PageSection from "@app/shared/PageSection";
import { SearchBox } from "@app/shared/SearchBox";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { WizardStepProps } from "@app/exceptions/contexts/interfaces";
import { VolumeReportsContext } from "@app/reports/volume-reports/import/VolumeReportsContext";

const VolumeReportsSelectSpecialties = (props: WizardStepProps): JSX.Element => {
  const wizardContext = useContext(VolumeReportsContext);

  useEffect(() => {
    // A specialty selection isn't strictly necessary, and it's possible to select a service area that has no common specialties
    props.pageIsValid(!wizardContext.loading && wizardContext.specialties.length > 0);
  }, [wizardContext.specialties]);

  const specialties = useMemo(() => {
    return wizardContext.specialties.filter((specialty) => specialty.isVisible).map((specialty) => (
      <li key={specialty.displayName}>
        <QESCheckbox
          label={specialty.displayName}
          checked={specialty.isChecked}
          testId={`volume-reports__specialty-checkbox--${specialty.displayName}`}
          onChange={(e) => wizardContext.updateSelectedSpecialty(specialty.displayName, e.target.checked)}
        />
      </li>
    ));
  }, [wizardContext.specialties]);

  const showNoSpecialtiesAlert = useMemo(() => {
    const hasServiceAreas = wizardContext.serviceAreas.some((state) => state.checkboxState !== CheckboxStateEnum.Unchecked);
    return hasServiceAreas && specialties.length === 0 && !wizardContext.loading;
  }, [wizardContext.serviceAreas, specialties, wizardContext.loading]);

  const updateSearchString = (value: string) => {
    wizardContext.updateSpecialtiesVisibility(value);
  }

  return (
    <PageSection>
      <div className="container mt-3">
        {showNoSpecialtiesAlert && <QESAlert type="warning" persist={true}>The service areas you selected do not have any specialty groups or projects in common.</QESAlert>}
        <SearchBox
          escapeSearchValue={true}
          fieldsetClass="row"
          inputLabel="Search Specialty Groups"
          onSubmit={(value: string) => updateSearchString(value)}
          testId="volume-reports-search-specialties"
        />
      </div>
      <div className="container">
        <div className="row">
          <div className="px-0">
            <div className="border">
              <div className="vr__check-all-container">
                <QESCheckbox
                  label="Specialties"
                  checked={wizardContext.specialties.every((sp) => sp.isChecked)}
                  indeterminate={wizardContext.specialties.some((sp) => sp.isChecked) && wizardContext.specialties.some((sp) => !sp.isChecked)}
                  onChange={(e) => wizardContext.updateSelectedSpecialty("", e.target.checked)}
                  testId="volume-reports__all-specialties-checkbox"
                  containerClassName="vr__check-all"
                />
              </div>
              <ul className="vr__list vr__list--specialty">
                {specialties}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </PageSection>
  );
};

export default VolumeReportsSelectSpecialties;
